// library imports
import { Component } from 'react';

import './Datatable.scss';
import { Button, Form, Modal, Overlay, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import { deleteApi, postApi } from '../services/apiHelper';
import { AssessmentIcon, BuildingIcon, ClientIcon, DateIcon, DeleteIcon, EditIcon, EmailIcon, FolderIcon, LocationIcon, PhoneIcon, PlusIcon } from '../icons/Icons';
import { clientDelete, siteDelete, projectDelete } from '../settings/apiConfig';
import moment from 'moment';

/**
 * generic table to render provided data in tabular format
 * 
 * @param config
 * 
 * @returns html code to rener table on specified api data
 * 
**/

export class Datatable extends Component {

    constructor(props) {
        super(props);

        this.state = {
            viewType: "list",
            tooltipConfig: { rowIndex: null, columnIndex: null },
            loading: false,
            field: this.props.config.field,
            filter: this.props.config.filter,
            sort: this.props.config.sort,
            pageSize: 9, // Default page size value
            allRows: this.props.gradeDataList ? this.props.gradeDataList : [], // Full data from API (for client-side pagination)
            rows: [], // Currently displayed rows (a slice of allRows)
            totalRows: 0, // Total no of records (allRows.length)
            currentPage: 1, // Default current page
            totalPages: 1, // Default max page
            previous: null,
            next: null,
            addOnfilters: [],
            actionModal: { show: false, target: null, row: null },
            showDeleteModal: { show: false, data: null },
            useClientPagination: true, // true = client-side pagination; false = server-side keyset pagination
        }
    }

    componentDidMount() {
        this.getTableData();
    }

    getRequestPayload = (previousPage = false, nextPage = false) => {
        const payload = {};

        if (Array.isArray(this.state.field) && this.state.field.length > 0) {
            payload['field'] = this.state.field;
        }

        if (Array.isArray(this.state.filter) && Array.isArray(this.state.addOnfilters)) {
            payload['filter'] = [...this.state.filter, ...this.state.addOnfilters];
        }

        if (this.state.sort) {
            payload['sort'] = this.state.sort;
        }

        // Always send page.size so the backend doesn't enforce a default limit
        if (this.props.config.pagination) {
            if (this.state.useClientPagination) {
                // Client-side pagination: request ALL records (large page size), then split locally
                payload["page"] = { "size": 10000 };
            } else {
                // Server-side keyset pagination
                payload["page"] = { "size": this.state.pageSize };

                if (nextPage && this.state.next) {
                    payload["page"]['next'] = this.state.next;
                } else if (previousPage && this.state.previous) {
                    payload["page"]['previous'] = this.state.previous;
                }
            }
        }

        return payload;
    };

    getTableData = (previousPage = false, nextPage = false) => {
        if (this.props.config.url) {
            this.setState({ loading: true });
            const payload = this.getRequestPayload(previousPage, nextPage);
            
            postApi(this.props.config.url, payload).then((response) => {
                const allRows = response.data.data || [];
                const page = response.data.page;
                let totalRows, totalPages, previous, next, visibleRows;
                const useClient = this.state.useClientPagination;


                if (useClient) {
                    // Client-side pagination: store all rows and compute pages locally
                    totalRows = allRows.length;
                    totalPages = Math.max(Math.ceil(totalRows / this.state.pageSize), 1);
                    previous = null;
                    next = null;

                    // Apply client-side page slice
                    const startIdx = (this.state.currentPage - 1) * this.state.pageSize;
                    const endIdx = startIdx + this.state.pageSize;
                    visibleRows = allRows.slice(startIdx, endIdx);


                } else {
                    // Server-side keyset pagination
                    if (typeof page !== 'undefined' && page !== null) {
                        if (page.total !== undefined && page.total !== null) {
                            totalRows = page.total;
                            totalPages = Math.ceil(page.total / (page.size || this.state.pageSize));
                        } else {
                            totalRows = allRows ? allRows.length : 0;
                            totalPages = 1;
                        }
                        previous = page.keyset?.previous || null;
                        next = page.keyset?.next || null;
                    } else {
                        totalRows = allRows ? allRows.length : 0;
                        totalPages = 1;
                        previous = null;
                        next = null;
                    }
                    visibleRows = allRows;
                }

                this.setState({ loading: false, allRows, rows: visibleRows, totalRows, totalPages, previous, next },
                    () => {
                        if (this.props.onDataLoaded) {
                            this.props.onDataLoaded(visibleRows);
                        }
                    }
                );
            }).catch(() => {
                this.setState({ loading: false });
            });
        }
    }

    showTooltip = (rowIdx, colIdx) => {
        return this.state.tooltipConfig.rowIndex === rowIdx && this.state.tooltipConfig.columnIndex === colIdx;
    };

    setTooltipConfig = (rowIdx, colIdx) => {
        this.setState({
            tooltipConfig: { rowIndex: rowIdx, columnIndex: colIdx },
        });
    };

    setPage = (pageCount) => {
        const newPage = pageCount > -1 ? this.state.currentPage + 1 : this.state.currentPage - 1;

        if (this.state.useClientPagination) {
            // Client-side: just slice from allRows, no API call
            this.setState({ currentPage: newPage }, () => {
                this.applyClientPage();
            });
        } else {
            this.setState({ currentPage: newPage }, () => {
                this.getTableData(pageCount < 0, pageCount > 0);
            });
        }
    };

    applyClientPage = () => {
        const startIdx = (this.state.currentPage - 1) * this.state.pageSize;
        const endIdx = Math.min(startIdx + this.state.pageSize, this.state.allRows.length);
        const visibleRows = this.state.allRows.slice(startIdx, endIdx);
        this.setState({ rows: visibleRows });

        if (this.props.onDataLoaded) {
            this.props.onDataLoaded(visibleRows);
        }
    };

    calculatePaginationSplitup = () => {
        let initial = ((this.state.currentPage - 1) * this.state.pageSize);
        let pageStart = initial + 1;
        let pageEnd;

        if (this.state.totalRows > (this.state.currentPage * this.state.pageSize)) {
            pageEnd = this.state.currentPage * this.state.pageSize;
        } else {
            pageEnd = this.state.totalRows;
        }

        return `${pageStart} - ${pageEnd}`;
    }

    sort = (field) => {
        const sort = this.state.sort[0];

        if (sort.field !== field) {
            return 'fa-exchange-alt unsort-90';
        } else if (sort['direction'] === 'asc') {
            return 'fa-long-arrow-alt-up';
        } else {
            return 'fa-long-arrow-alt-down';
        }
    };

    handleSort = (column) => {
        if (column.sort) {
            let direction = '';
            if (this.state.sort[0]['field'] === column['property'] && this.state.sort[0]['direction'] === 'asc') {
                direction = 'desc'
            } else {
                direction = 'asc';
            }
            this.setState({
                sort: [{ field: column['property'], direction: direction }],
                currentPage: 1,
                next: null,
                previous: null
            }, () => {
                this.getTableData();
            });
        }
    };

    renderColumn = (row, column) => {
        if (column['customColumn']) {
            return column['customColumn'](column['property'] ? row[column['property']] : row);
        } else if (column['property'] in row) {
            return row[column['property']] ? row[column['property']] : '- - - -';
        } else if (column?.actions?.row && column?.actions?.row.length > 0) {
            return column.actions.row.map((elem, index) => {
                if (elem.type === 'commonAction') {
                    return this.renderCommonActions(row, index);
                } else if (elem.type === 'custom') {
                    return elem.action(row);
                }
                return (
                    <img key={"action-" + index} src={`${process.env.PUBLIC_URL}/static/icons/table/${elem.type}.svg`} alt={elem.type} className="action-button" onClick={() => { elem.action(row.id); }} />
                )
            });
        }
    };

    renderCommonActions = (row, index) => {
        return (
            <img key={"action-" + index} src={`${process.env.PUBLIC_URL}/static/images/more.png`} alt="more" className="action-button"
                onClick={(e) => {
                    e.stopPropagation();
                    this.setState({
                        actionModal: {
                            show: true,
                            target: e.currentTarget,
                            row
                        }
                    });
                }}
            />
        );
    };

    searchData = (addOnfilters=[]) => {
        addOnfilters.forEach(element => {
            element.value = typeof element.op === 'string' && element.op.toLowerCase() === 'like' ? `*${element.value}*` : element.value
        });
        
        if (Array.isArray(addOnfilters)) {
            this.setState({
                currentPage: 1,
                next: null,
                previous: null,
                addOnfilters
            }, () => {
                this.getTableData();
            });
        }
    };

    selectAllCheckBox = () => {
        return (
            <Form>
                <Form.Check className="d-flex align-items-center justify-content-center" type="checkbox" name="selectall" value="selectall" />
            </Form>
        );
    }

    allItemsSelect = (event) => {
        this.props.allItemsSelect(event);
    }

    navigateToLink = (path) => {
        if ( path ) {
            this.props.history.push(path);
        }
    };

    navigateToEdit = (row) => {
        if ( this.props.config.name === "clients" ) {
            this.props.history.push(`/clients/edit/${row.id}`);
        } else if ( this.props.config.name === "sites" ) {
            this.props.history.push(`/sites/${row.id}`);
        } else if ( this.props.config.name === "projects" ) {
            // Completed projects go to the detail/read-only page
            if (row.status?.toLowerCase() === "completed") {
                this.props.history.push(`/projects/${row.id}`);
            } else {
                this.props.history.push(`/projects/edit/${row.id}`);
            }
        }
    }

    navigateToSiteEdit = (row) => {
        this.props.history.push(`/sites/edit/${row.id}`);
    }

    navigateToDelete = (data) => {
        if ( this.props.config.name === "clients" || this.props.config.name === "projects" || this.props.config.name === "sites" ) {
            this.setState({ showDeleteModal: { show: true, data: data } });
        } 
    }

    deleteItem = (data) => {
        let url;
        if ( this.props.config.name === "clients" ) {
            url = clientDelete + data.id;
        } else if ( this.props.config.name === "sites" ) {
            url = siteDelete + data.id;
        } else {
            url = projectDelete + data.id;
        }
        deleteApi(url).then(response => {
            if (response.status === 200) {
                this.setState({
                    showDeleteModal: { show: false, data: null }
                });
                this.getTableData();
            }
        }).catch((error) => {
            console.error("Error deleting:", error);
        });
    }

    renderPagination = () => {
        const { loading, rows, totalRows, currentPage, totalPages } = this.state;

        if (loading || rows.length === 0) {
            return null;
        }

        return (
            <div className="d-flex flex-column flex-sm-row align-items-center justify-content-sm-between px-2 py-4" style={{ width: '100%' }}>
                <div className="bottom-align">Showing <b>{this.calculatePaginationSplitup()}</b> of <b>{totalRows}</b> records</div>
                <div className="d-flex m-3 m-sm-0 pageview" style={{ cursor: 'default', border: '1px solid rgb(107 75 161 / 20%)' }}>
                    <i className={`d-flex align-items-center justify-content-center fa fa-angle-double-left text-n page-switch ${!loading && currentPage !== 1 ? 'switch-enable' : 'switch-disable'}`} onClick={() => !loading && currentPage !== 1 ? this.setPage(-1) : null} />
                    <div style={{ borderLeft: '1px solid #rgb(107 75 161 / 20%)', borderRight: '1px solid rgb(107 75 161 / 20%)', padding: '4px 12px' }}>Page {currentPage}</div>
                    <i className={`d-flex align-items-center justify-content-center fa fa-angle-double-right text-n page-switch ${!loading && currentPage !== totalPages ? 'switch-enable' : 'switch-disable'}`} onClick={() => !loading && currentPage !== totalPages ? this.setPage(1) : null} />
                </div>
            </div>
        );
    };

    renderGridView = () => {
        const { rows, loading } = this.state;

        if (!loading && rows.length === 0) {
            if(this.props.config.name === "clients") {
                return (
                    <div className="d-flex flex-column align-items-center justify-content-center gap-3 activity-card dashboard-container p-5">
                        <div className="file-icon">
                            <ClientIcon color={"#6b4ba1"} size={35} />
                        </div>
                        <div className="text-1l">No clients yet</div>
                        <span className="text-muted">Add your first client to start creating projects.</span>
                        <div  className="btn btn-solid-blue fw-bold" onClick={() => this.navigateToLink('/clients/add')}>
                            <PlusIcon />
                            Add First Client
                        </div>
                    </div>
                )
            } else if (this.props.config.name === "sites") {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center gap-3 activity-card dashboard-container p-5">
                    <div className="file-icon">
                        <BuildingIcon color={"#6b4ba1"} size={35} />
                    </div>
                    <div className="text-1l">No sites yet</div>
                    <span className="text-muted">Add your first site to start managing locations.</span>
                    <div  className="btn btn-solid-blue fw-bold" onClick={() => this.navigateToLink('/sites/add')}>
                        <PlusIcon />
                        Add First Site
                    </div>
                </div>
            )
        } else if (this.props.config.name === "projects") {
                return (
                    <div className="d-flex flex-column align-items-center justify-content-center gap-3 activity-card dashboard-container p-5">
                        <div className="file-icon">
                            <AssessmentIcon color={"#6b4ba1"} size={35} />
                        </div>
                        <div className="text-1l">No projects yet</div>
                        <span className="text-muted">Create your first project to get started.</span>
                        <div  className="btn btn-solid-blue fw-bold" onClick={() => this.navigateToLink('/projects/select-type')}>
                            <PlusIcon />
                            Create First Project
                        </div>
                    </div>
                )
            }
        }

        return (
            <>
                <div className="row g-4">
                    {this.props.config.name === "clients" ? 
                        rows.map((a, index) => (
                                <div className="col-lg-4 col-md-6 col-sm-12" key={index}>
                                    <div className="card metric-card-view h-100 border-0 rounded-4 p-3" style={{ boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 3px 0px, rgba(0, 0, 0, 0.1) 0px 1.5px 5px 0px", padding: "0px" }}>
                                        <div className="d-flex justify-content-between">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="gradient-profile">{`${a?.name?.charAt(0) || ''}`}</div>
                                                <div className="d-flex flex-column">
                                                    <div className="fw-semibold">{a.name}</div>
                                                    <div className='d-flex align-items-center gap-2'>
                                                        <BuildingIcon size={14}/>{a.company_name}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='d-flex gap-3'>
                                                <div onClick={() => this.navigateToEdit(a)}><EditIcon color="#9333ea" size={14} /></div>
                                                <div onClick={(e) => { e.stopPropagation(); this.navigateToDelete(a); }}><DeleteIcon color="red" size={14} /></div>
                                            </div>
                                        </div>

                                    <div className="d-flex flex-column text-n mt-2 lh-lg">
                                        <div className="d-flex align-items-center gap-2 text-gray"><EmailIcon size="17px" color={"hsl(260 10% 45%)"} />{a.email || '- - -'}</div>
                                        <div className="d-flex align-items-center gap-2 text-gray"><PhoneIcon size="17px" color={"hsl(260 10% 45%)"} />{a.mobile || '- - -'}</div>
                                        <div className="d-flex align-items-center gap-2 text-gray"><FolderIcon size="17px" color={"hsl(260 10% 45%)"} />{a.address || '- - -'}</div>
                                    </div>
                                </div>
                            </div>
                        )) : 
                        this.props.config.name === "sites" ? 
                            rows.map((a, index) => (
                                <div className="col-lg-4 col-md-6 col-sm-12" key={index} onClick={() => this.navigateToEdit(a)}>
                                    <div className="card metric-card-view h-100 border-0 rounded-4 p-3 gap-3" style={{ border: "1px solid rgba(107, 75, 161, 0.2)", boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(107, 75, 161, 0.1)", padding: "0px"  }}>
                                        <div className='d-flex align-items-center justify-content-between'>
                                            <div className="p-2 rounded total-icon" style={{ width: "fit-content" }}>
                                                <BuildingIcon color={"#FFF"}/>
                                            </div>
                                            <div className='d-flex gap-3'>
                                                <div onClick={(e) => { e.stopPropagation(); this.navigateToSiteEdit(a); }}><EditIcon color="#9333ea" size={14} /></div>
                                                <div onClick={(e) => { e.stopPropagation(); this.navigateToDelete(a); }}><DeleteIcon color="red" size={14} /></div>
                                            </div>
                                        </div>
                                        <div className="fw-semibold">{a.name}</div>
                                        <div className='d-flex align-items-center gap-2 text-gray'>
                                            <LocationIcon size={14} />
                                            {a.location || '- - -'}
                                        </div>
                                    </div>
                                </div>
                            )) : 
                        this.props.config.name === "projects" ? 
                            rows.map((a, index) => (
                                <div className="col-lg-4 col-md-6 col-sm-12" key={index} onClick={() => this.navigateToEdit(a)}>
                                    <div className="card metric-card-view h-100 border-0 rounded-4 p-3 gap-3" style={{ border: "1px solid rgba(107, 75, 161, 0.2)", boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(107, 75, 161, 0.1)", padding: "0px"  }}>
                                        <div className='d-flex align-items-center justify-content-between'>
                                            <div className="p-2 rounded total-icon" style={{ width: "fit-content" }}>
                                                <BuildingIcon color={"#FFF"}/>
                                            </div>
                                            <span className={`status-badge ${(a.status || a.runstatus || '')?.toLowerCase() === 'completed' ? 'status-badge-green' : ''}`}>{a.runstatus || a.status || '- - -'}</span>
                                        </div>
                                        <div className="fw-semibold">{a.name}</div>
                                        <div className='d-flex align-items-center justify-content-between'>
                                            <div className='d-flex align-items-center gap-2'>
                                                <DateIcon size={14} />
                                                {a.created_at ? moment.utc(a.created_at).tz("Asia/Kolkata").format('YYYY-MM-DD') : '- - -'}
                                            </div>
                                            <div onClick={(e) => { e.stopPropagation(); this.navigateToDelete(a); }}>
                                                <DeleteIcon color="red" size={14} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )) : null
                    }

                </div>
                {this.renderPagination()}
            </>
        );
    };

    render() {
        return (
            <div className="datatable-outer-wrapper">
                {   
                    this.props.viewType === 'grid' ? 
                    this.renderGridView() : 
                    <div className="w-100 table-responsive datatable-scroll-container" style={{ position: 'relative', overflowY: 'hidden', borderRadius: "10px" }}>
                        <table className="table table-box list-card-view">
                            <thead className="table-head">
                                <tr className="row-height">
                                    {
                                        this.props.config.columns.map((column, colIdx) =>
                                            <th key={colIdx}
                                                style={{ minWidth: column['width'], width: column['width'], color: 'dimgrey', padding: "1rem" }}
                                                className={`table-heading`}>
                                                {
                                                    column?.actions?.header ? column.actions.header : column['name']
                                                }
                                                {
                                                    column['sort'] ? <i className={`fa ${this.sort(column['property'])} clickable`} style={{ margin: '0 4px' }} onClick={() => { this.handleSort(column); }}></i> : null
                                                }
                                                {
                                                    column['customHeader'] ? column['customHeader'] === 'selectAll' ? this.selectAllCheckBox() : column['customHeader'](this.state.rows)  : ''
                                                }
                                            </th>
                                        )
                                    }
                                </tr>
                            </thead>
                            <tbody className="table-body">
                                {
                                    !this.state.loading && this.state.rows.length > 0 ?
                                        this.state.rows.map((row, rowIdx) =>
                                            <tr key={`data_${rowIdx}`} className={`row-height row-bg-even row-bg-even-hover`} onClick={() => { this.navigateToEdit(row); }}>
                                                {
                                                    this.props.config.columns.map((column, colIdx) =>
                                                        <td key={`column_${colIdx}`}
                                                            style={{ width: column['width'], padding: "1rem" }}
                                                            className={`${column.class ? column.class : ''}`}
                                                            onMouseEnter={column.showTooltip ? () => { this.setTooltipConfig(rowIdx, colIdx); } : null}
                                                            onMouseLeave={column.showTooltip ? () => { this.setTooltipConfig(null, null); } : null}>
                                                            <OverlayTrigger overlay={<Tooltip>{row[column['property']]}</Tooltip>} show={row[column['property']] ? this.showTooltip(rowIdx, colIdx) : false} placement="top">
                                                                <div>{this.renderColumn(row, column)}</div>
                                                            </OverlayTrigger>
                                                        </td>
                                                    )
                                                }
                                            </tr>
                                        ) : 
                                        <tr style={{ height: '500px' }}>
                                            <td colSpan={this.props.config.columns.length}>
                                                <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height: '100%' }}>
                                                    {
                                                        this.state.loading ? <img className='datatable-loader-img' src={`${process.env.PUBLIC_URL}/static/images/loader.svg`} alt="loader-icon" />
                                                        : 'No results found, please try your search again'
                                                    }
                                                </div>
                                            </td>
                                        </tr>
                                }
                            </tbody>
                        </table>
                    </div>}

                {
                      !this.state.loading && this.state.rows.length > 0 && this.props.viewType !== 'grid' ?
                        <div className="d-flex flex-column flex-sm-row align-items-center justify-content-sm-between px-2 py-4" style={{ width: '100%' }}>
                            <div className="bottom-align">Showing <b>{this.calculatePaginationSplitup()}</b> of <b>{this.state.totalRows}</b> records</div>
                            <div className="d-flex m-3 m-sm-0 pageview" style={{ cursor: 'default', border: '1px solid rgb(107 75 161 / 20%)' }}>
                                <i className={`d-flex align-items-center justify-content-center fa fa-angle-double-left text-n page-switch ${!this.state.loading && this.state.currentPage !== 1 ? 'switch-enable' : 'switch-disable'}`} onClick={() => !this.state.loading && this.state.currentPage !== 1 ? this.setPage(-1) : null} />
                                <div style={{ borderLeft: '1px solid #rgb(107 75 161 / 20%)', borderRight: '1px solid rgb(107 75 161 / 20%)', padding: '4px 12px' }}>Page {this.state.currentPage}</div>
                                <i className={`d-flex align-items-center justify-content-center fa fa-angle-double-right text-n page-switch ${!this.state.loading && this.state.currentPage !== this.state.totalPages ? 'switch-enable' : 'switch-disable'}`} onClick={() => !this.state.loading && this.state.currentPage !== this.state.totalPages ? this.setPage(1) : null} />
                            </div>
                        </div> :
                        null

                }

                {
                    this.state.showDeleteModal.show ? 
                    <Modal centered show={this.state.showDeleteModal.show} size={"sm"} animation={true}>
                        <Modal.Body>
                            <div className='d-flex align-items-center justify-content-center'>
                                Are you sure you want to delete {this.state.showDeleteModal.data?.name || `this ${this.props.config.name === "projects" ? "project" : this.props.config.name === "sites" ? "site" : "client"}`}?
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="d-flex justify-content-center w-100 gap-3">
                                <Button variant="secondary" onClick={() => this.setState({ showDeleteModal: { show: false, data: null } })}> Cancel </Button>
                                <Button variant="primary" onClick={() => this.deleteItem(this.state.showDeleteModal.data)}> {this.props.config.name === "projects" ? "Delete Project" : this.props.config.name === "sites" ? "Delete Site" : "Delete Client"} </Button>
                            </div>
                        </Modal.Footer>
                    </Modal> : null
                }

                <Overlay show={this.state.actionModal.show} target={this.state.actionModal.target} placement="left" containerPadding={10} rootClose onHide={() => this.setState({ actionModal: { show: false, target: null, row: null } })}>
                    <Popover className="action-popover">
                        <Popover.Body className="p-0">
                            {this.props.onEdit && (
                                <div className="action-item gap-2"
                                     onClick={() => {
                                        this.props.onEdit(this.state.actionModal.row);
                                        this.setState({ actionModal: { show: false, target: null, row: null } });
                                    }}>
                                    <img width="15px" src={`${process.env.PUBLIC_URL}/static/images/edit.png`} alt="edit" /> Edit
                                </div>
                            )}
                            {this.props.onAssign && (
                                <div className="action-item gap-2"
                                    onClick={() => {
                                        this.props.onAssign(this.state.actionModal.row);
                                        this.setState({ actionModal: { show: false, target: null, row: null } });
                                    }}>
                                    <img width="15px" src={`${process.env.PUBLIC_URL}/static/images/assign.png`} alt="assign" /> Assign
                                </div>
                            )}

                            {this.props.onDelete && (
                                <div className="action-item gap-2"
                                    onClick={() => {
                                        this.props.onDelete(this.state.actionModal.row);
                                        this.setState({ actionModal: { show: false, target: null, row: null } });
                                    }}>
                                    <img width="15px" src={`${process.env.PUBLIC_URL}/static/images/delete.png`} alt="delete" /> Delete
                                </div>
                            )}
                        </Popover.Body>
                    </Popover>
                </Overlay>
            </div>
        );
    }
}